import { useEffect, useEffectEvent, useState } from "react";

import type { TranslationLookup } from "@/translations/implementation/createT";
import type { TranslationLookupRegistry } from "@/translations/implementation/TranslationLookupRegistry";

export type PickedTranslation = {
  text: string;
  lookups: readonly TranslationLookup[];
};

type HoveredTranslation = PickedTranslation & {
  registry: TranslationLookupRegistry;
};

interface TranslationPickerState {
  hovered: HoveredTranslation | null;
  selection: PickedTranslation | null;
  clearSelection: () => void;
}

/** Subscribes to browser picking events only while the inspector's explicit pick mode is active. */
export function useTranslationPicker(
  registry: TranslationLookupRegistry,
  enabled: boolean,
  onStop: () => void,
): TranslationPickerState {
  const [hovered, setHovered] = useState<HoveredTranslation | null>(null);
  const [selection, setSelection] = useState<
    (PickedTranslation & { registry: TranslationLookupRegistry; element: Element }) | null
  >(null);
  const stop = useEffectEvent(onStop);
  const hoveredRegistry = hovered?.registry;
  const selectionRegistry = selection?.registry;

  // Capture browser events before React controls handle them: picking must not follow links or submit forms.
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const isInspector = (target: EventTarget | null) =>
      target instanceof Element && Boolean(target.closest("[data-translation-inspector]"));
    const find = (target: EventTarget | null) => findTranslation(target, registry.getSnapshot());
    let highlighted: Element | null = null;
    const clearHover = () => {
      highlighted?.removeAttribute("data-inspection-highlight");
      highlighted = null;
      setHovered(null);
    };
    const preview = (event: Event) => {
      if (isInspector(event.target)) {
        return;
      }
      const match = find(event.target);
      highlighted?.removeAttribute("data-inspection-highlight");
      highlighted = match?.element ?? null;
      highlighted?.setAttribute("data-inspection-highlight", "");
      setHovered(match ? { text: match.text, lookups: match.lookups, registry } : null);
    };
    const block = (event: Event) => {
      if (!isInspector(event.target)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    const pick = (event: Event) => {
      if (isInspector(event.target)) {
        return;
      }
      block(event);
      const match = find(event.target);
      if (match) {
        setSelection({ text: match.text, lookups: match.lookups, registry, element: match.element });
        stop();
      }
    };
    const keyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
        stop();
      } else if ((event.key === "Enter" || event.key === " ") && !isInspector(event.target)) {
        pick(event);
      }
    };

    document.addEventListener("pointerover", preview, true);
    document.addEventListener("focusin", preview, true);
    document.addEventListener("pointerdown", block, true);
    document.addEventListener("mousedown", block, true);
    document.addEventListener("click", pick, true);
    document.addEventListener("auxclick", block, true);
    document.addEventListener("keydown", keyDown, true);
    window.addEventListener("scroll", clearHover, true);
    window.addEventListener("resize", clearHover);
    window.addEventListener("blur", clearHover);

    return () => {
      highlighted?.removeAttribute("data-inspection-highlight");
      document.removeEventListener("pointerover", preview, true);
      document.removeEventListener("focusin", preview, true);
      document.removeEventListener("pointerdown", block, true);
      document.removeEventListener("mousedown", block, true);
      document.removeEventListener("click", pick, true);
      document.removeEventListener("auxclick", block, true);
      document.removeEventListener("keydown", keyDown, true);
      window.removeEventListener("scroll", clearHover, true);
      window.removeEventListener("resize", clearHover);
      window.removeEventListener("blur", clearHover);
    };
  }, [enabled, registry]);

  // The second click of a double-click arrives after picking stops; it must not activate the selected control.
  useEffect(() => {
    if (!selection || selectionRegistry !== registry) {
      return;
    }
    const blockRepeatedClick = (event: MouseEvent) => {
      if (event.detail > 1 && event.target instanceof Node && selection.element.contains(event.target)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    document.addEventListener("click", blockRepeatedClick, true);
    document.addEventListener("dblclick", blockRepeatedClick, true);

    const removeListeners = () => {
      document.removeEventListener("click", blockRepeatedClick, true);
      document.removeEventListener("dblclick", blockRepeatedClick, true);
    };
    const timeout = window.setTimeout(removeListeners, 500);

    return () => {
      window.clearTimeout(timeout);
      removeListeners();
    };
  }, [registry, selection, selectionRegistry]);

  return {
    hovered: enabled && hoveredRegistry === registry ? hovered : null,
    selection: selectionRegistry === registry ? selection : null,
    clearSelection: (): void => {
      setSelection(null);
      setHovered(null);
    },
  };
}

/** Finds candidates by rendered text, retaining ambiguity instead of assigning an arbitrary key. */
function findTranslation(target: EventTarget | null, lookups: readonly TranslationLookup[]) {
  if (
    !(target instanceof Element) ||
    target.closest("[data-translation-inspector], input, textarea, select, [contenteditable]")
  ) {
    return null;
  }

  let element: Element | null = target;
  for (let depth = 0; element && depth < 5; depth += 1, element = element.parentElement) {
    if (element.matches("body, html, main, script, style, svg")) {
      break;
    }
    const text = normalize(element.textContent ?? "");
    if (!text || text.length > 2000) {
      continue;
    }
    const matches = lookups.filter(
      (lookup) => !lookup.key.startsWith("component.translation-inspector.") && matchesText(text, lookup),
    );
    if (matches.length > 0) {
      return { element, text, lookups: matches };
    }
  }

  return null;
}

function normalize(value: string) {
  return value
    .replaceAll(/<\/?[a-z][^>]*>/gi, "")
    .replaceAll(/\s+/g, " ")
    .trim();
}

function matchesText(text: string, lookup: TranslationLookup) {
  if (text === normalize(lookup.value)) {
    return true;
  }
  // A key can render several counts/names on one page; the registry retains its latest resolved value.
  const template = normalize(lookup.template);
  const pieces = template.split(/{{\s*\w+\s*}}/g);
  if (pieces.length < 2 || pieces.join("").trim().length < 3) {
    return false;
  }
  if (!text.startsWith(pieces[0])) {
    return false;
  }
  let position = pieces[0].length;
  for (const piece of pieces.slice(1, -1)) {
    const next = text.indexOf(piece, position + 1);
    if (next < 0) {
      return false;
    }
    position = next + piece.length;
  }
  const last = pieces[pieces.length - 1];

  return text.endsWith(last) && text.length - last.length > position;
}
